import { CalendarDays, Clock, Loader2 } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import useDividendSchedule from '@/hooks/useDividendSchedule';

const money = (v) => (v == null ? '--' : `$${Number(v).toFixed(4)}`);

const agoLabel = (iso) => {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : `${Math.round(mins / 60)} h ago`;
};

function Row({ label, value, defKey, testId }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
      <span className="text-xs font-bold text-muted-foreground">{defKey ? <AbbrTip term={defKey}>{label}</AbbrTip> : label}</span>
      <span className="text-xs font-mono font-bold" data-testid={testId}>{value ?? '--'}</span>
    </div>
  );
}

function Card({ icon: Icon, title, badge, children, foot, testId }) {
  return (
    <div className="rounded-lg border bg-card p-3 space-y-1" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <h4 className="text-xs font-bold">{title}</h4>
        {badge}
      </div>
      <div>{children}</div>
      {foot && <p className="text-[10px] leading-snug text-muted-foreground pt-1">{foot}</p>}
    </div>
  );
}

const Pill = ({ tone, children, testId }) => (
  <span
    className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${tone}`}
    data-testid={testId}
  >
    {children}
  </span>
);

export default function DividendScheduleCards({ ticker }) {
  const { data, loading } = useDividendSchedule(ticker);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dividend-schedule-loading">
        {[0, 1].map((i) => (
          <div key={i} className="rounded-lg border bg-card p-3 h-[132px] flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ))}
      </div>
    );
  }
  if (!data?.has_history) {
    return (
      <div className="rounded-lg border bg-card p-3 text-xs text-muted-foreground" data-testid="dividend-schedule-empty">
        Massive has no dividend payments on record for {ticker} yet.
      </div>
    );
  }

  const { last, next, frequency, fetched_at } = data;
  const change = next?.declared && next.change_pct != null && Math.abs(next.change_pct) >= 1 ? next.change_pct : null;
  const checked = agoLabel(fetched_at);

  return (
    <div className="space-y-1.5" data-testid="dividend-schedule">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card
          icon={Clock}
          title="Last Payment"
          testId="dividend-schedule-last"
          badge={last && !last.paid && <Pill tone="bg-sky-500/15 text-sky-700 dark:text-sky-300" testId="dividend-schedule-last-pending">pays {last.pay_date_human}</Pill>}
        >
          <Row label="Ex-Dividend Date" defKey="EX_DIVIDEND_DATE" value={last?.ex_date_human} testId="dividend-schedule-last-ex" />
          <Row label="Payment Date" defKey="PAYMENT_DATE" value={last?.pay_date_human} testId="dividend-schedule-last-pay" />
          <Row label="Amount Per Share" value={money(last?.amount)} testId="dividend-schedule-last-amount" />
          <Row label="Frequency" value={frequency} testId="dividend-schedule-last-freq" />
        </Card>

        <Card
          icon={CalendarDays}
          title="Next Payment"
          testId="dividend-schedule-next"
          badge={next && (next.declared
            ? <Pill tone="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" testId="dividend-schedule-next-declared">declared</Pill>
            : <Pill tone="bg-amber-500/15 text-amber-700 dark:text-amber-300" testId="dividend-schedule-next-estimated">estimated</Pill>)}
          foot={next && (next.declared
            ? (next.went_ex ? `Went ex-dividend ${next.ex_date_human} · cash lands ${next.pay_date_human}.` : `Declared by the issuer and published by Massive.`)
            : `${next.basis}${next.based_on_cycles ? ` Based on ${next.based_on_cycles} past ${next.based_on_cycles === 1 ? 'cycle' : 'cycles'}${next.based_on_cycles < 4 ? ' — treat as a rough guess.' : '.'}` : ''} Estimates can be wrong — confirm the issuer's declaration before buying for the dividend; not buying advice.`)}
        >
          <Row label="Ex-Dividend Date" defKey="EX_DIVIDEND_DATE" value={next?.ex_date_human} testId="dividend-schedule-next-ex" />
          <Row label="Payment Date" defKey="PAYMENT_DATE" value={next?.pay_date_human} testId="dividend-schedule-next-pay" />
          <Row
            label={next?.declared ? 'Declared Amount Per Share' : 'Est. Amount Per Share'}
            testId="dividend-schedule-next-amount"
            value={(
              <span className="inline-flex items-center gap-1.5">
                {money(next?.amount)}
                {change != null && (
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${change < 0
                      ? 'bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400'
                      : 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400'}`}
                    title={`Declared ${change < 0 ? 'cut' : 'raise'} versus the last payment of ${money(last?.amount)}`}
                    data-testid="dividend-schedule-next-change"
                  >
                    {change > 0 ? '+' : ''}{change.toFixed(1)}%
                  </span>
                )}
              </span>
            )}
          />
          <Row label="Frequency" value={frequency} testId="dividend-schedule-next-freq" />
        </Card>
      </div>
      <p className="text-[10px] text-muted-foreground text-right" data-testid="dividend-schedule-source">
        Massive dividend feed{checked ? ` · checked ${checked}` : ''}
      </p>
    </div>
  );
}
